// =============================================================================
// colors.ts - Color palette with light/dark mode support
// =============================================================================
// Plain object export (NOT a class). Structured as:
//   primitives: raw hex values from Figma color primitives
//   semantic:   light/dark mode tokens referencing primitives
//   shadows:    elevation shadow definitions
//   gradients:  CSS gradient strings from Figma fill styles
// =============================================================================

// ---------------------------------------------------------------------------
// Primitive Colors - raw hex values from Figma "Color / Primitives" collection
// These are the base palette and should NOT be used directly in components.
// Components should always use semantic tokens.
// ---------------------------------------------------------------------------
const primitives = {
  neutral0: "#ffffff",
  neutral100: "#f9fafc",
  neutral200: "#f1f1f1",
  neutral300: "#d0d0d0",
  neutral400: "#9e9e9e",
  neutral500: "#6d6d6d",
  neutral600: "#000000",
  primary100: "#fdcfbf",
  primary200: "#fcb79f",
  primary300: "#fa9f7e",
  primary400: "#f9875e",
  primary500: "#f86f3e",
  primary600: "#cf5d34",
  primary700: "#a54a29",
  primary800: "#7c381f",
  primary900: "#532515",
  secondary100: "#fff7f0",
  secondary200: "#ffd9b7",
  secondary300: "#ffcc9e",
  secondary400: "#ffc086",
  secondary500: "#ffb36e",
  secondary600: "#d5955c",
  secondary700: "#aa7749",
  secondary800: "#805a37",
  secondary900: "#553c25",
  warning100: "#f6dfcf",
  warning200: "#f2cfb7",
  warning300: "#edbe9e",
  warning400: "#e9ae86",
  warning500: "#e49e6e",
  warning600: "#be845c",
  warning700: "#986949",
  warning800: "#724f37",
  warning900: "#4c3525",
  error100: "#edb6b2",
  error200: "#e4928c",
  error300: "#db6d65",
  error400: "#d2493f",
  error500: "#c92418",
  error600: "#a81e14",
  error700: "#861810",
  error800: "#65120c",
  error900: "#430c08",
  success100: "#b9d5ba",
  success200: "#97c098",
  success300: "#74ab76",
  success400: "#519653",
  success500: "#2e8131",
  success600: "#266c29",
  success700: "#1f5621",
  success800: "#174119",
  success900: "#0f2b10",
  // EGDE-327: hyperlink blue for rich-text body links. The design system has
  // no blue primitive; this matches the Figma accessibility-statement link
  // colour and clears WCAG AA on white (~4.6:1).
  linkBlue: "#2563eb",
} as const;

// ---------------------------------------------------------------------------
// Semantic Colors - Light Mode
// Maps design intent (background, text, border, button states) to primitives.
// ---------------------------------------------------------------------------
const lightMode = {
  bg: {
    primary: primitives.neutral0,
    secondary: primitives.neutral200,
    tertiary: primitives.neutral200,
    inverse: primitives.neutral500,
    brand: primitives.primary500,
    success: primitives.success500,
    warning: primitives.warning500,
    error: primitives.error500,
    disabled: primitives.neutral100,
  },
  text: {
    primary: primitives.neutral600,
    secondary: primitives.neutral0,
    tertiary: primitives.neutral600,
    disabled: primitives.neutral500,
    brand: primitives.primary800,
    success: primitives.success500,
    warning: primitives.warning500,
    error: primitives.error500,
    inverse: primitives.neutral600,
  },
  border: {
    default: primitives.neutral300,
    hover: primitives.neutral400,
    focus: primitives.neutral500,
    disabled: primitives.neutral300,
    brand: primitives.primary500,
    success: primitives.success500,
    warning: primitives.warning500,
    error: primitives.error500,
  },
  button: {
    primaryDefault: primitives.secondary500,
    primaryHover: primitives.primary500,
    primaryPress: primitives.primary600,
    primaryDisabled: primitives.neutral200,
    secondaryDefault: primitives.neutral0,
    secondaryHover: primitives.primary500,
    secondaryPress: primitives.primary600,
    secondaryDisabled: primitives.neutral200,
    opacityDefault: primitives.neutral0,
    opacityHover: primitives.primary500,
    opacityPress: primitives.primary600,
    opacityDisabled: primitives.neutral200,
  },
  inputs: {
    dropDownDefault: primitives.neutral300,
    dropDownHover: primitives.neutral400,
    dropDownFocus: primitives.primary600,
    dropDownError: primitives.error500,
    dropDownFilled: primitives.neutral300,
    dropDownDisabled: primitives.neutral300,
    defaultDefault: primitives.neutral300,
    defaultHover: primitives.neutral400,
    defaultFocus: primitives.neutral500,
    defaultError: primitives.error500,
    defaultFilled: primitives.neutral300,
    defaultDisabled: primitives.neutral300,
    defaultWarning: primitives.warning500,
    defaultSuccess: primitives.success500,
    searchDefault: primitives.neutral300,
    searchHover: primitives.neutral400,
    searchFocus: primitives.neutral500,
    searchError: primitives.error500,
    searchFilled: primitives.neutral300,
    searchDisabled: primitives.neutral300,
    searchWarning: primitives.warning500,
    searchSuccess: primitives.success500,
    dropDownSuccess: primitives.success500,
    dropDownWarning: primitives.warning500,
  },
} as const;

// ---------------------------------------------------------------------------
// Semantic Colors - Dark Mode
// Same structure as light mode but with dark-appropriate values.
// ---------------------------------------------------------------------------
const darkMode = {
  bg: {
    primary: primitives.neutral600,
    secondary: primitives.neutral500,
    tertiary: primitives.neutral500,
    inverse: primitives.neutral200,
    brand: primitives.primary800,
    success: primitives.success800,
    warning: primitives.warning800,
    error: primitives.error100,
    disabled: primitives.neutral100,
  },
  text: {
    primary: primitives.neutral0,
    secondary: primitives.neutral0,
    tertiary: primitives.neutral0,
    disabled: primitives.neutral200,
    brand: primitives.secondary200,
    success: primitives.success100,
    warning: primitives.warning100,
    error: primitives.error100,
    inverse: primitives.neutral0,
  },
  border: {
    default: primitives.neutral100,
    hover: primitives.neutral200,
    focus: primitives.neutral200,
    disabled: primitives.neutral300,
    brand: primitives.primary100,
    success: primitives.success100,
    warning: primitives.warning100,
    error: primitives.error100,
  },
  button: {
    primaryDefault: primitives.secondary500,
    primaryHover: primitives.primary500,
    primaryPress: primitives.primary600,
    primaryDisabled: primitives.neutral300,
    secondaryDefault: primitives.neutral0,
    secondaryHover: primitives.primary500,
    secondaryPress: primitives.primary600,
    secondaryDisabled: primitives.neutral300,
    opacityDefault: "rgba(0, 0, 0, 0.5)",
    opacityHover: primitives.primary500,
    opacityPress: primitives.primary600,
    opacityDisabled: primitives.neutral300,
  },
  inputs: {
    dropDownDefault: primitives.neutral100,
    dropDownHover: primitives.neutral200,
    dropDownFocus: primitives.primary600,
    dropDownError: primitives.error100,
    dropDownFilled: primitives.neutral100,
    dropDownDisabled: primitives.neutral300,
    defaultDefault: primitives.neutral100,
    defaultHover: primitives.neutral200,
    defaultFocus: primitives.neutral200,
    defaultError: primitives.error100,
    defaultFilled: primitives.neutral100,
    defaultDisabled: primitives.neutral300,
    defaultWarning: primitives.warning100,
    defaultSuccess: primitives.success100,
    searchDefault: primitives.neutral100,
    searchHover: primitives.neutral200,
    searchFocus: primitives.neutral200,
    searchError: primitives.error100,
    searchFilled: primitives.neutral100,
    searchDisabled: primitives.neutral300,
    searchWarning: primitives.warning100,
    searchSuccess: primitives.success100,
    dropDownSuccess: primitives.success100,
    dropDownWarning: primitives.warning100,
  },
} as const;

// ---------------------------------------------------------------------------
// Shadow definitions - elevation levels from Figma effects
// ---------------------------------------------------------------------------
const shadows = {
  none: "none",
  stripWhite: "0px 8px 24px 0px rgba(25, 25, 25, 0.12)",
  shadowHeader: "0px 8px 24px 0px rgba(25, 25, 25, 0.12)",
  serviceCta: "0px 8px 24px 0px rgba(25, 25, 25, 0.12)",
  // EGDE-324: the button-library component in Figma applies a tighter 12px-blur
  // drop shadow on its hover/click states (not the 24px "Strip White" token used
  // for the header/form surfaces). Matching the actual button component.
  button: "0px 8px 12px 0px rgba(25, 25, 25, 0.12)",
} as const;

// ---------------------------------------------------------------------------
// Gradient definitions - CSS gradient strings from Figma fill styles
// ---------------------------------------------------------------------------
const gradients = {
  gradientCta: "linear-gradient(90deg, rgba(255, 179, 110, 1) 0%, rgba(248, 111, 62, 1) 100%)",
  mainGradient: "linear-gradient(90deg, rgba(255, 179, 110, 1) 0%, rgba(248, 111, 62, 1) 100%)",
} as const;

// ---------------------------------------------------------------------------
// Backward-compatible dropShadow sub-keys
// Maps from the legacy sharedColors.dropShadow shape to normalized shadows
// ---------------------------------------------------------------------------
const dropShadow = {
  main: shadows.stripWhite,
  mainSmall: shadows.serviceCta,
  light: shadows.none,
  header: shadows.shadowHeader,
} as const;

// ---------------------------------------------------------------------------
// Exported colors object
// ---------------------------------------------------------------------------
export const colors = {
  primitives,
  gradients,
  light: lightMode,
  dark: darkMode,
  shadows,
  dropShadow,
} as const;

// ---------------------------------------------------------------------------
// Backward compatibility: sharedColors
// Many consumers import { sharedColors } from "ui/theme/colors"
// ---------------------------------------------------------------------------
/** @deprecated Use `colors` instead */
export const sharedColors = {
  brandMain: primitives.primary500,
  brandDark: primitives.primary600,
  mediumGrayLight: primitives.neutral200,
  labelGray: primitives.neutral500,
  borderGray: primitives.neutral300,
  black: primitives.neutral600,
  red: primitives.error500,
  accessibleGray: primitives.neutral500,
  white: primitives.neutral0,
  // Legacy color names (preserved for backward compat)
  beige: "#F3F3EE",
  gray: "#75787B",
  lightGray: "#E6E5EA",
  bluishGray: "#C4CEE3",
  blue: "#395AA1",
  darkBlue: "#141B4D",
  lightBlue: "#87B0D6",
  turquoise: "#00A7B5",
  lightTurquoise: "#B3E5E9",
  lightRed: "#EBB7C1",
  darkOrange: "#DA291C",
  orange: "#ED7D25",
  lightOrange: "#F6BD90",
  pink: "#B52555",
  lightPink: "#E9BECC",
  dropShadow,
} as const;

// Type exports for use in types.ts and consuming components
export type Colors = typeof colors;
export type ColorPrimitives = typeof primitives;
export type SemanticColors = typeof lightMode;
export type Shadows = typeof shadows;
export type Gradients = typeof gradients;
